import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Calendar, Users, DollarSign, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SplitPaymentPage() {
  const { bookingId, paymentLinkId } = useParams();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        const res = await axios.get(`${API}/split-payment/${bookingId}/${paymentLinkId}`);
        setPaymentInfo(res.data);
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError(err.response?.data?.detail || 'Failed to load payment information');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [bookingId, paymentLinkId]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/split-payment/${bookingId}/${paymentLinkId}/checkout`, {
        origin_url: window.location.origin
      });
      
      if (res.data?.url) {
        toast.success('Redirecting to secure payment...');
        setTimeout(() => {
          window.location.href = res.data.url;
        }, 500);
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast.error(err.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-secondary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-primary mb-2">Payment Link Error</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentInfo?.already_paid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-primary mb-2">Already Paid</h2>
            <p className="text-muted-foreground">This payment has already been completed. Thank you!</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">Pay Your Share</h1>
          <p className="text-muted-foreground">
            {paymentInfo?.lead_booker} has invited you to join this trip
          </p>
        </div>

        <Card className="mb-6">
          {paymentInfo?.trip_image && (
            <div className="h-48 overflow-hidden rounded-t-lg">
              <img 
                src={paymentInfo.trip_image} 
                alt={paymentInfo.trip_title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-xl">{paymentInfo?.trip_title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-5 w-5 text-secondary" />
              <span>{paymentInfo?.tour_date}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Users className="h-5 w-5 text-secondary" />
              <span>Traveler {paymentInfo?.traveler_num} of {paymentInfo?.total_travelers}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <DollarSign className="h-5 w-5 text-secondary" />
              <span>Booked by: {paymentInfo?.lead_booker}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-secondary/30 bg-secondary/5">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground mb-1">Your Share</p>
              <p className="text-4xl font-bold text-primary">
                ${paymentInfo?.amount?.toLocaleString()}
              </p>
            </div>

            <Button
              onClick={handlePayment}
              disabled={processing}
              className="w-full bg-secondary hover:bg-secondary/90 text-white py-6 text-lg"
              data-testid="pay-share-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>Pay ${paymentInfo?.amount?.toLocaleString()} Now</>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Secure payment powered by Stripe
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
